import { Box, Chip, capitalize, Popover, Stack } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useApiMutation } from "../hooks/use-api-mutation";
import { useUpdateFlow } from "../hooks/use-flows";
import type { PersistedUserFlow, Test } from "../types";
import { CHIP_COLOR_OVERRIDES, getChipColors } from "../utils/chip-colors";
import { clickableProps } from "../utils/keyboard";

// TODO: Move to types.ts
interface TestCase {
  id: string;
  name: string;
  type: "flow" | "test";
  status: string;
  priority: string;
  severity: string;
  created_at: string;
  created_by: string;
  source?: string;
  test_id?: string;
  flow_data?: PersistedUserFlow;
  test_data?: Test;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case "critical":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "info";
    default:
      return "default";
  }
};

export const EditableSeverityCell = (params: GridRenderCellParams<TestCase>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState(params.value);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { mutate: updateTest } = useApiMutation<{ name: string; severity: string }, void>(
    "PUT",
    "",
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Severity updated successfully", {
          variant: "success",
        });
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating severity: ${error.message}`, {
          variant: "error",
        });
        setValue(params.value); // Reset to original value
        handleClose();
      },
    },
  );

  const { mutate: updateFlow } = useUpdateFlow(params.row.flow_data?.id || "");

  const handleOptionSelect = (newValue: string) => {
    setValue(newValue);
    if (params.row.type === "test" && params.row.test_data) {
      updateTest({
        _url: `/tests/${params.row.test_data.id}`,
        name: params.row.name, // Keep existing name
        severity: newValue,
      });
    } else if (params.row.type === "flow" && params.row.flow_data) {
      updateFlow({
        severity: newValue as "low" | "medium" | "high" | "critical",
      });
    }
    handleClose();
  };

  const severityOptions = [
    { value: "low", label: "Low", color: "default" as const },
    { value: "medium", label: "Medium", color: "info" as const },
    { value: "high", label: "High", color: "warning" as const },
    { value: "critical", label: "Critical", color: "error" as const },
  ];

  const severityColor = getSeverityColor(value);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          cursor: "pointer",
          width: "100%",
          height: "100%",
          position: "relative",
        }}
        {...clickableProps((event: unknown) => {
          (event as React.MouseEvent).stopPropagation?.();
          setAnchorEl((event as React.MouseEvent).currentTarget as HTMLElement);
        })}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <Chip
          label={capitalize(value)}
          variant="outlined"
          size="small"
          color={
            severityColor as
              | "default"
              | "primary"
              | "secondary"
              | "error"
              | "info"
              | "success"
              | "warning"
          }
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            height: 24,
            "& .MuiChip-label": {
              px: 1.5,
            },
            borderWidth: 1.5,
            ...CHIP_COLOR_OVERRIDES,
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            width: "auto",
            minWidth: 120,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Stack spacing={0.5}>
            {severityOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                variant={value === option.value ? "filled" : "outlined"}
                size="small"
                color={option.color}
                onClick={() => handleOptionSelect(option.value)}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  height: 24,
                  cursor: "pointer",
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                  borderWidth: 1.5,
                  ...(() => {
                    const colors = getChipColors(option.color);
                    return {
                      backgroundColor: value === option.value ? colors.bg : "transparent",
                      borderColor: colors.border,
                      color: colors.text,
                      "&:hover": { backgroundColor: colors.bg },
                    };
                  })(),
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default EditableSeverityCell;
